import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { SERVICES } from "../lib/siteData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
    name: "",
    email: "",
    phone: "",
    service: "",
    property_type: "",
    budget: "",
    location: "",
    message: "",
};

const PROPERTY_TYPES = [
    "Condominium",
    "Landed / Terrace",
    "Semi-D / Bungalow",
    "Retail / Office",
    "Other",
];

const BUDGETS = [
    "Under RM 30,000",
    "RM 30,000 – 80,000",
    "RM 80,000 – 150,000",
    "RM 150,000 – 300,000",
    "Above RM 300,000",
];

export const EnquiryForm = ({ compact = false }) => {
    const [form, setForm] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const validate = () => {
        if (!form.name.trim()) return "Please share your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            return "Enter a valid email address.";
        if (form.phone.trim().length < 6) return "Enter a valid phone number.";
        if (!form.message.trim()) return "Tell us a little about your project.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Thank you — we'll reply within 48 hours.");
            setDone(true);
            setForm(initialState);
        } catch (e2) {
            toast.error("Something went wrong. Please try WhatsApp instead.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                className="rounded-2xl border border-hhc-line bg-hhc-cream/50 p-10 md:p-14 text-center"
                data-testid="enquiry-success"
            >
                <div className="eyebrow">Enquiry received</div>
                <h3 className="font-display text-3xl md:text-4xl mt-4 text-hhc-ink">
                    We'll be in touch shortly.
                </h3>
                <p className="mt-4 text-hhc-muted max-w-md mx-auto">
                    Our studio replies within 48 hours on business days. For urgent
                    briefs, message us on WhatsApp.
                </p>
                <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="btn-outline mt-8 text-[13px]"
                    data-testid="enquiry-send-another"
                >
                    Send another enquiry
                </button>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className={`grid grid-cols-1 ${
                compact ? "" : "md:grid-cols-2"
            } gap-x-8 gap-y-6`}
            data-testid="enquiry-form"
        >
            <Field label="Full name" required>
                <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    data-testid="enquiry-name"
                    className={inputCls}
                />
            </Field>
            <Field label="Email" required>
                <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    data-testid="enquiry-email"
                    className={inputCls}
                />
            </Field>
            <Field label="Phone / WhatsApp" required>
                <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+60 12 345 6789"
                    data-testid="enquiry-phone"
                    className={inputCls}
                />
            </Field>
            <Field label="Location">
                <input
                    type="text"
                    value={form.location}
                    onChange={update("location")}
                    placeholder="e.g. Mont Kiara, KL"
                    data-testid="enquiry-location"
                    className={inputCls}
                />
            </Field>
            <Field label="Service">
                <select
                    value={form.service}
                    onChange={update("service")}
                    data-testid="enquiry-service"
                    className={inputCls}
                >
                    <option value="">Choose a service</option>
                    {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>
                            {s.title}
                        </option>
                    ))}
                </select>
            </Field>
            <Field label="Property type">
                <select
                    value={form.property_type}
                    onChange={update("property_type")}
                    data-testid="enquiry-property"
                    className={inputCls}
                >
                    <option value="">Select type</option>
                    {PROPERTY_TYPES.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>
            </Field>
            <Field label="Budget">
                <select
                    value={form.budget}
                    onChange={update("budget")}
                    data-testid="enquiry-budget"
                    className={inputCls}
                >
                    <option value="">Estimated budget</option>
                    {BUDGETS.map((b) => (
                        <option key={b} value={b}>
                            {b}
                        </option>
                    ))}
                </select>
            </Field>
            <div className="md:col-span-2">
                <Field label="Tell us about your project" required>
                    <textarea
                        value={form.message}
                        onChange={update("message")}
                        rows={5}
                        placeholder="Rooms involved, timeline, and anything else we should know…"
                        data-testid="enquiry-message"
                        className={`${inputCls} resize-y min-h-[140px]`}
                    />
                </Field>
            </div>

            <div className="md:col-span-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-2">
                <p className="text-[12px] text-hhc-muted max-w-md leading-relaxed">
                    By sending this enquiry you agree we may contact you via email,
                    phone, or WhatsApp to discuss your project.
                </p>
                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary text-[13px] disabled:opacity-70"
                    data-testid="enquiry-submit"
                >
                    {submitting ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <ArrowRight size={16} strokeWidth={1.75} />
                    )}
                    {submitting ? "Sending" : "Send enquiry"}
                </button>
            </div>
        </form>
    );
};

const inputCls =
    "w-full bg-transparent border-b border-hhc-line focus:border-hhc-clay pb-2 text-[15px] text-hhc-ink placeholder:text-hhc-muted/60 outline-none transition-colors";

const Field = ({ label, required, children }) => (
    <label className="flex flex-col gap-2">
        <span className="text-[11px] tracking-[0.24em] uppercase text-hhc-muted font-semibold">
            {label}
            {required && <span className="text-hhc-clay">*</span>}
        </span>
        {children}
    </label>
);

export default EnquiryForm;
